import React, { useState, useEffect, useRef } from 'react';
import XCircleIcon from '../icons/XCircleIcon';
import { useSoftKeyboard } from '../../contexts/SoftKeyboardContext';

interface ItemNoteModalProps {
  isOpen: boolean;
  itemName: string;
  currentNote?: string;
  onClose: () => void;
  onSave: (note: string) => void;
}

const ItemNoteModal: React.FC<ItemNoteModalProps> = ({
  isOpen,
  itemName,
  currentNote = '',
  onClose,
  onSave,
}) => {
  const [note, setNote] = useState(currentNote);
  const noteRef = useRef<string>(currentNote); // Keep a ref to track the latest value
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { openKeyboard, enabled: softKeyboardEnabled, closeKeyboard } = useSoftKeyboard();

  // Update ref whenever note changes - this keeps ref in sync with state
  // But we also update ref directly in onChange, so this is a backup
  useEffect(() => {
    noteRef.current = note;
  }, [note]);

  // Track if this is the first time the modal is opening (not a re-render)
  const isFirstOpen = useRef(true);
  
  // Initialize note when modal opens (only on first open, not on re-renders)
  useEffect(() => {
    if (isOpen) {
      // Only initialize if this is the first time opening
      // Don't reset if user has typed something
      if (isFirstOpen.current) {
        const initialNote = currentNote || '';
        setNote(initialNote);
        noteRef.current = initialNote;
        isFirstOpen.current = false;
      } else {
        // Modal is already open, just ensure we have the latest value
        // Don't reset - preserve user input
      }
      
      // Remove the handled attribute when modal opens so keyboard can appear again
      // Use setTimeout to ensure DOM is ready
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.removeAttribute('data-soft-keyboard-handled');
        }
      }, 0);
    } else {
      // Modal is closing
      closeKeyboard();
      // Reset the first open flag for next time
      isFirstOpen.current = true;
      // Don't reset noteRef here - preserve the value in case modal reopens
    }
  }, [isOpen, closeKeyboard]);

  // Update note when currentNote prop changes (but ONLY on initial open)
  // This prevents resetting user-typed content during re-renders
  useEffect(() => {
    // Only update if modal just opened (isFirstOpen) AND currentNote is different
    // This prevents overwriting user input during re-renders
    if (isOpen && isFirstOpen.current && currentNote !== undefined && currentNote !== noteRef.current) {
      setNote(currentNote);
      noteRef.current = currentNote;
    }
  }, [currentNote, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    // Use ref value as it's always the most up-to-date
    const valueToSave = noteRef.current || note;
    onSave(valueToSave.trim());
    closeKeyboard();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto my-auto">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-bold text-gray-800">Add Note for {itemName}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircleIcon className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-4">
          <label htmlFor="itemNote" className="block text-sm font-medium text-gray-700 mb-2">
            Special Instructions
          </label>
          <textarea
            ref={textareaRef}
            id="itemNote"
            value={note || noteRef.current || ''}
            onChange={(e) => {
              if (!softKeyboardEnabled) {
                setNote(e.target.value);
              }
            }}
            onFocus={() => {
              if (softKeyboardEnabled) {
                // Remove the handled attribute to allow keyboard to open again
                if (textareaRef.current) {
                  textareaRef.current.removeAttribute('data-soft-keyboard-handled');
                }
                // Always use the ref value to ensure we have the absolute latest text
                // The ref is updated synchronously, so it's more reliable than state
                const currentValue = noteRef.current || '';
                openKeyboard({
                  id: 'item-note-textarea',
                  type: 'alphanumeric',
                  value: currentValue,
                  label: 'Special Instructions',
                  onChange: (value) => {
                    // Update both state and ref immediately
                    // This is called on every keystroke, so state should always be in sync
                    setNote(value);
                    noteRef.current = value;
                  },
                  onSubmit: () => {
                    // The onChange should have already been called with the final buffer value
                    // But let's ensure the state is updated from the ref as a safety measure
                    // Use the ref value directly since it's always up to date
                    const finalValue = noteRef.current;
                    setNote(finalValue);
                    noteRef.current = finalValue; // Ensure ref is also set
                    
                    // Use requestAnimationFrame to ensure React has processed the state update
                    requestAnimationFrame(() => {
                      // Double-check the value is still correct
                      if (noteRef.current !== finalValue) {
                        setNote(noteRef.current);
                      }
                      
                      // Blur the textarea when Done is clicked
                      setTimeout(() => {
                        if (textareaRef.current) {
                          textareaRef.current.blur();
                        }
                      }, 10);
                    });
                  },
                });
              }
            }}
            readOnly={softKeyboardEnabled}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-500 bg-gray-100 text-gray-900 placeholder-gray-500"
            placeholder="e.g., No onions, Extra spicy, Well done..."
            rows={4}
            autoFocus
          />
          <p className="text-xs text-gray-500 mt-2">
            Add special instructions for this item that the kitchen should know.
          </p>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition-colors"
          >
            Save Note
          </button>
        </div>
      </div>
    </div>
  );
};

export default ItemNoteModal;

